<template>
    <footer class="nch--footer" :style="$bgColor(color.primary)">

        <!-- Main body: 3 columns -->
        <div v-if="hasContactInfo || hasSocials || reverseListMenu"
            :class="['nch--footer-body', footer && footer.footContainer ? 'container' : 'container-fluid']">
            <!-- Col 1: contact info -->
            <div v-if="hasContactInfo" class="nch--footer-col nch--footer-col--contact">
                <p class="nch--footer-section-title" :style="{ color: color.secondary }">Contato</p>

                <div v-if="address && address.address" class="nch--footer-contact-row">
                    <i class="fas fa-map-marker-alt" :style="{ color: color.secondary }"></i>
                    <a :href="mapsUrl" target="_blank" rel="noopener noreferrer"
                        :style="{ color: color.secondary }" class="nch--footer-link">
                        {{ [address.address, address.number, address.complement, address.neighborhood, address.city,
                        address.state].filter(Boolean).join(', ') }}
                    </a>
                </div>

                <div v-if="telephones && telephones.length">
                    <div v-for="(tel, i) in telephones" :key="i" class="nch--footer-contact-row">
                        <i class="fas fa-phone" :style="{ color: color.secondary }"></i>
                        <span :style="{ color: color.secondary }">
                            {{ tel.label ? `${tel.label}: ` : '' }}{{ formatPhone(tel.value) }}
                        </span>
                    </div>
                </div>

                <div v-if="address && address.email" class="nch--footer-contact-row">
                    <i class="fas fa-envelope" :style="{ color: color.secondary }"></i>
                    <a :href="`mailto:${address.email}`" :style="{ color: color.secondary }" class="nch--footer-link">
                        {{ address.email }}
                    </a>
                </div>

                <div v-if="address && address.whatsapp" class="nch--footer-contact-row">
                    <i :class="address.whatsapp_type === 'phone' ? 'fas fa-phone' : 'fab fa-whatsapp'"
                       :style="{ color: color.secondary }"></i>
                    <a :href="address.whatsapp_type === 'phone'
                            ? `tel:${address.whatsapp.replace(/\D/g, '')}`
                            : `https://wa.me/55${address.whatsapp.replace(/\D/g, '')}`"
                        :target="address.whatsapp_type === 'phone' ? '_self' : '_blank'"
                        :style="{ color: color.secondary }" class="nch--footer-link">
                        {{ address.whatsapp }}
                    </a>
                </div>

                <div v-if="hasSocials" class="nch--footer-socials nch--footer-socials--inline">
                    <a v-for="(social, i) in socials" :key="i" :href="social.url" target="_blank"
                        rel="noopener noreferrer" class="nch--footer-social-link">
                        <i :class="social.icon" :style="{ color: color.secondary }"></i>
                    </a>
                </div>
            </div>

            <!-- Col 2: menu -->
            <div v-if="reverseListMenu" class="nch--footer-col nch--footer-col--social">
                <NavbarCustomMenu footer :menu="reverseListMenu" />
            </div>
        </div>

        <!-- Bottom bar: copyright only (when no contact info) OR divider line -->
        <div v-if="!hasContactInfo && !hasSocials && !reverseListMenu"
            :class="['nch--footer-bottom nch--footer-bottom--simple', footer && footer.footContainer ? 'container' : 'container-fluid']">
            <span :style="{ color: color.secondary }">{{ `©${new Date().getFullYear()} — ${copyright || 'Nochalks'}`
                }}</span>
        </div>

        <div v-if="hasContactInfo || hasSocials || reverseListMenu" class="nch--footer-bar"
            :style="{ 'background-color': 'rgba(0,0,0,0.15)' }">
            <div :class="footer && footer.footContainer ? 'container' : 'container-fluid'"
                class="nch--footer-bar-inner">
                <span class="nch--footer-bar-text" :style="{ color: color.secondary + 'aa' }">
                    {{ `©${new Date().getFullYear()} — ${copyright || 'Nochalks'}` }}
                </span>
            </div>
        </div>

    </footer>
</template>

<script>
import BaseButton from '../argon/components/BaseButton'
import NavbarCustomMenu from './NavbarComponents/NavbarCustomMenu'

export default {
    name: 'Footer',
    props: {
        color: {
            type: Object,
        },
        socials: {
            type: Array
        },
        customMenu: Array,
        copyright: String,
        footer: Object,
        address: {
            type: Object,
            default: null
        },
        telephones: {
            type: Array,
            default: () => []
        }
    },
    components: {
        BaseButton,
        NavbarCustomMenu
    },
    computed: {
        reverseListMenu() {
            if (this.customMenu) {
                return [...this.customMenu].reverse();
            }
            return null;
        },
        hasContactInfo() {
            return (this.address && (this.address.address || this.address.email || this.address.whatsapp))
                || (this.telephones && this.telephones.length > 0);
        },
        hasSocials() {
            return this.socials && this.socials.length > 0;
        },
        mapsUrl() {
            if (!this.address || !this.address.address) return '#';
            const parts = [this.address.address, this.address.number, this.address.neighborhood, this.address.city, this.address.state];
            const query = encodeURIComponent(parts.filter(Boolean).join(', '));
            return `https://www.google.com/maps/search/?api=1&query=${query}`;
        }
    },
    methods: {
        formatPhone(value) {
            if (!value) return value;
            // já tem formatação (parênteses ou espaço) → retorna como está
            if (/[() ]/.test(value)) return value;
            const digits = value.replace(/\D/g, '');
            if (digits.length === 8) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
            if (digits.length === 9) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
            if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
            if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
            return value;
        }
    }
}
</script>

<style scoped>
.nch--footer {
    width: 100%;
    margin-top: auto;
}

/* Main 3-col body */
.nch--footer-body {
    display: flex;
    flex-wrap: wrap;
    gap: 2rem 4rem;
    padding: 2.5rem 2rem 2rem;
    max-width: 1200px;
    margin: 0 auto;
}

.nch--footer-col {
    display: flex;
    flex-direction: column;
    min-width: 200px;
}

.nch--footer-col--contact {
    flex: 1 1 300px;
    max-width: 560px;
}

.nch--footer-col--social {
    flex: 0 0 auto;
    align-items: flex-start;
}

.nch--footer-section-title {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    opacity: 0.6;
    margin-bottom: 0.75rem;
}

.nch--footer-contact-row {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
    line-height: 1.4;
}

.nch--footer-contact-row i {
    margin-top: 0.2rem;
    width: 14px;
    flex-shrink: 0;
    font-size: 0.85rem;
    opacity: 0.85;
}

.nch--footer-link {
    text-decoration: none;
    opacity: 0.9;
    transition: opacity 0.15s;
}

.nch--footer-link:hover {
    opacity: 1;
    text-decoration: underline;
}

.nch--footer-socials {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1rem;
}

.nch--footer-social-link {
    font-size: 1.25rem;
    opacity: 0.85;
    transition: opacity 0.15s;
}

.nch--footer-social-link:hover {
    opacity: 1;
}

/* Bottom copyright bar */
.nch--footer-bar {
    padding: 0.6rem 0;
}

.nch--footer-bar-inner {
    display: flex;
    justify-content: center;
    padding-left: 2rem;
    padding-right: 2rem;
    max-width: 1200px;
    margin: 0 auto;
}

.nch--footer-bar-text {
    font-size: 0.8rem;
    text-align: center;
}

.nch--footer-socials--inline {
    margin-top: 0.5rem;
    gap: 0.75rem;
}

/* Simple footer (no contact info) */
.nch--footer-bottom--simple {
    padding: 1rem 1.5rem;
    font-size: 0.875rem;
}


@media (max-width: 768px) {
    .nch--footer-body {
        flex-direction: column;
        gap: 1.5rem;
        padding: 2rem 1rem 1.5rem;
    }

    .nch--footer-col {
        /* no empilhamento (column), zera a min-width pensada p/ layout em linha */
        min-width: 0;
    }

    .nch--footer-col--contact {
        /* em row o flex-basis 300px era largura; em column virava ALTURA e
           forçava a coluna a 300px, criando um vão branco até o copyright.
           Aqui a coluna passa a ter a altura do próprio conteúdo. */
        flex: 0 0 auto;
        max-width: 100%;
    }

    .nch--footer-col--social {
        align-items: flex-start;
    }

    /* coluna de menu vazia (sem menu custom) não ocupa espaço */
    .nch--footer-col--social:empty {
        display: none;
    }
}
</style>
